import React, { useState } from "react";
import { Menu, X, LayoutDashboard } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Logo from "./Logo";
import { useCMS } from "../context/CMSContext";
import { useAuth } from "../context/AuthContext";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { cms } = useCMS();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const brand = cms?.site?.brandName || "Live Mover";
  const nav = cms?.nav || [];
  const portalHref = user ? (user.role === "admin" ? "/admin" : "/portal") : "/login";
  const portalLabel = user ? (user.role === "admin" ? "Admin" : "My Account") : "Login";
  const onHome = location.pathname === "/";

  const handleNavClick = (e, href) => {
    if (!href || !href.startsWith("#")) return;
    e.preventDefault();
    setOpen(false);
    if (onHome) {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate("/" + href);
    }
  };

  return (
    <div className="sticky top-2 sm:top-3 z-40 px-3 sm:px-4">
      <div className="max-w-6xl mx-auto lm-nav-pill rounded-full px-3 sm:px-5 py-2.5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group flex-shrink-0">
          <Logo size={36} />
          <span className="lm-font font-extrabold text-[#11151C] text-base sm:text-lg tracking-tight">
            {brand}
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {nav.map((item) => (
            <a
              key={item.label}
              href={onHome || !item.href?.startsWith("#") ? item.href : `/${item.href}`}
              onClick={(e) => handleNavClick(e, item.href)}
              className="px-3.5 py-2 rounded-full text-sm font-medium text-[#11151C]/80 hover:text-[#11151C] hover:bg-[#11151C]/5 transition-colors inline-flex items-center gap-1.5"
            >
              {item.label}
              {item.badge && (
                <span className="bg-[#FF6B3D] text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
                  {item.badge}
                </span>
              )}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <Link
            to={portalHref}
            className="px-3 py-2 text-sm font-semibold text-[#11151C] hover:text-[#FF6B3D] transition-colors inline-flex items-center gap-1.5"
          >
            {user && <LayoutDashboard className="w-4 h-4" />}
            {portalLabel}
          </Link>
          <a
            href={onHome ? "#pricing" : "/#pricing"}
            onClick={(e) => handleNavClick(e, "#pricing")}
            className="lm-btn-primary px-4 py-2.5 rounded-full text-sm font-semibold"
          >
            Get Started
          </a>
        </div>

        <button
          className="md:hidden p-2 text-[#11151C]"
          onClick={() => setOpen(!open)}
          aria-label="menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden max-w-6xl mx-auto mt-2 lm-nav-pill rounded-2xl p-3 space-y-1">
          {nav.map((item) => (
            <a
              key={item.label}
              href={onHome || !item.href?.startsWith("#") ? item.href : `/${item.href}`}
              onClick={(e) => handleNavClick(e, item.href)}
              className="flex items-center justify-between px-3 py-2.5 text-[#11151C] font-medium rounded-lg hover:bg-[#11151C]/5"
            >
              <span>{item.label}</span>
              {item.badge && (
                <span className="bg-[#FF6B3D] text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
                  {item.badge}
                </span>
              )}
            </a>
          ))}
          <Link
            to={portalHref}
            onClick={() => setOpen(false)}
            className="block px-3 py-2.5 text-[#11151C] font-semibold rounded-lg hover:bg-[#11151C]/5"
          >
            {portalLabel}
          </Link>
          <a
            href={onHome ? "#pricing" : "/#pricing"}
            onClick={(e) => handleNavClick(e, "#pricing")}
            className="lm-btn-primary block text-center px-4 py-2.5 rounded-full text-sm font-semibold mt-2"
          >
            Get Started
          </a>
        </div>
      )}
    </div>
  );
}

